import { useDebounce } from '@/hooks/use-debounce';
import { isMobile, isTablet } from '@/utils/devices';
import { type RefObject, useCallback, useEffect, useState } from 'react';

export const useLobbyMask = (
    container: RefObject<HTMLDivElement | null>,
    wrapper?: RefObject<HTMLDivElement | null>
) => {
    const [maskStyle, setMaskStyle] = useState({ maskImage: 'unset', WebkitMaskImage: 'unset' });
    const [isMaskVisible, setIsMaskVisible] = useState(false);

    const fadeWidth = 60;
    const boxShadowHeight = 16;
    const fadeOffset = 25;

    const isMobileOrTablet = isMobile() || isTablet();

    const getHorizontalMaskGradient = (fadeStart: number, fadeEnd: number, direction: 'left' | 'right' = 'left') => {
        if (direction === 'left') {
            // Left fade: opaque on left, transparent on right
            return `linear-gradient(to right,
            rgba(0, 0, 0, 1) 0,
            rgba(0, 0, 0, 1) ${fadeStart}px,
            rgba(0, 0, 0, 0.6) ${fadeStart + fadeOffset}px,
            rgba(0, 0, 0, 0) ${fadeEnd}px,
            rgba(0, 0, 0, 0) ${fadeEnd}px
        )`;
        }
        // Right fade: transparent on left, opaque on right
        return `linear-gradient(to right,
        rgba(0, 0, 0, 0) 0,
        rgba(0, 0, 0, 0) ${fadeStart}px,
        rgba(0, 0, 0, 0.6) ${fadeStart + fadeOffset}px,
        rgba(0, 0, 0, 1) ${fadeEnd}px,
        rgba(0, 0, 0, 0) ${fadeEnd}px
    )`;
    };

    const getHorizontalMaskGradientCombined = (
        leftFadeStart: number,
        leftFadeEnd: number,
        rightFadeStart: number,
        rightFadeEnd: number
    ) => `linear-gradient(to right,
            rgba(0, 0, 0, 0) 0,
            rgba(0, 0, 0, 0) 0,
            rgba(0, 0, 0, 1) ${leftFadeStart}px,
            rgba(0, 0, 0, 0.6) ${leftFadeStart + fadeOffset}px,
            rgba(0, 0, 0, 0) ${leftFadeEnd}px,
            rgba(0, 0, 0, 0) ${rightFadeStart}px,
            rgba(0, 0, 0, 0.6) ${rightFadeStart + fadeOffset}px,
            rgba(0, 0, 0, 1) ${rightFadeEnd}px,
            rgba(0, 0, 0, 0) ${rightFadeEnd}px
        )`;

    const getVerticalMaskGradient = (fadeStart: number, fadeEnd: number) => {
        return `linear-gradient(to bottom, 
            rgba(0, 0, 0, 0) 0,
            rgba(0, 0, 0, 0) ${fadeStart}px,
            rgba(0, 0, 0, 1) ${fadeStart}px,
            rgba(0, 0, 0, 1) ${fadeStart + fadeEnd}px,
            rgba(0, 0, 0, 0) ${fadeStart + fadeEnd}px,
            rgba(0, 0, 0, 0) 100%
        )`;
    };

    const setMaskImage = useCallback(() => {
        if (!container.current || isMobileOrTablet) {
            setIsMaskVisible(false);
            return;
        }

        // Recalculate isBeginning and isEnd inside the function with current scroll state
        const currentIsBeginning = container.current.scrollLeft === 0;
        const currentIsEnd =
            container.current.scrollLeft + container.current.clientWidth >= container.current.scrollWidth - 1;

        // Hide fade if all content fits (no scrolling needed)
        if (currentIsBeginning && currentIsEnd) {
            setIsMaskVisible(false);
            return;
        }

        const containerRect = container.current.getBoundingClientRect();

        if (!wrapper?.current) {
            setIsMaskVisible(false);
            return;
        }

        const wrapperRect = wrapper.current.getBoundingClientRect();
        const containerTopRelative = containerRect.top - wrapperRect.top;
        const containerBottomRelative = containerRect.bottom - wrapperRect.top;
        const gapToDraggableContainer = containerRect.height - wrapperRect.height;
        const siteWideJackpotModule = wrapper.current.closest('.site-wide-jackpot-module') as HTMLElement;
        let wrapperOffsetFromModuleTop = 0;

        if (siteWideJackpotModule) {
            const moduleRect = siteWideJackpotModule.getBoundingClientRect();
            wrapperOffsetFromModuleTop = wrapperRect.top - moduleRect.top;
        }

        let verticalStartOffset: number;

        if (containerTopRelative > 0) {
            verticalStartOffset = containerTopRelative - boxShadowHeight;
        } else {
            verticalStartOffset = gapToDraggableContainer + wrapperOffsetFromModuleTop - boxShadowHeight;
        }

        const verticalMaskGradient = getVerticalMaskGradient(
            verticalStartOffset,
            containerBottomRelative + boxShadowHeight
        );

        if (currentIsBeginning && !currentIsEnd) {
            // At beginning, can scroll right - show fade on right only
            const horizontalGradient = getHorizontalMaskGradient(
                Math.floor(containerRect.width + containerRect.x - fadeWidth),
                Math.ceil(containerRect.x + containerRect.width),
                'right'
            );

            setMaskStyle({
                maskImage: `${verticalMaskGradient}, ${horizontalGradient}`,
                WebkitMaskImage: `${verticalMaskGradient}, ${horizontalGradient}`
            });
            setIsMaskVisible(true);
        } else if (!currentIsBeginning && currentIsEnd) {
            // At end, can scroll left - show fade on left only
            const horizontalGradient = getHorizontalMaskGradient(
                Math.floor(containerRect.x),
                Math.ceil(containerRect.x + fadeWidth),
                'left'
            );

            setMaskStyle({
                maskImage: `${verticalMaskGradient}, ${horizontalGradient}`,
                WebkitMaskImage: `${verticalMaskGradient}, ${horizontalGradient}`
            });
            setIsMaskVisible(true);
        } else if (!currentIsBeginning && !currentIsEnd) {
            // In middle, can scroll both ways - show fade on both sides
            const horizontalGradient = getHorizontalMaskGradientCombined(
                Math.floor(containerRect.x),
                Math.floor(containerRect.x + fadeWidth),
                Math.ceil(containerRect.width + containerRect.x - fadeWidth),
                Math.ceil(containerRect.x + containerRect.width)
            );

            setMaskStyle({
                maskImage: `${verticalMaskGradient}, ${horizontalGradient}`,
                WebkitMaskImage: `${verticalMaskGradient}, ${horizontalGradient}`
            });
            setIsMaskVisible(true);
        } else {
            setIsMaskVisible(false);
        }
    }, [container, wrapper, isMobileOrTablet]);

    const resize = useDebounce(() => setMaskImage(), 200);

    useEffect(() => {
        window.addEventListener('resize', resize);

        return () => {
            window.removeEventListener('resize', resize);
        };
    }, []);

    return { maskStyle, isMaskVisible, setMaskImage };
};
